import { z } from 'zod';
import { ApiClient } from '../../../lib/api/ApiClient.types';
import {
  buildFilterConverter,
  convertBooleanFilterToApi,
  convertStringFilterToApi
} from '../../platform/filters/filterConverters';
import { Callout } from '../../../__types__/v1/models/Callout';
import {
  CalloutsApi,
  CalloutsListQuery,
  CalloutsQuery,
  TransferCalloutsToWorkspaceRequest
} from './callouts.api';
import {
  callout$,
  calloutFromHttp,
  calloutAssociations$,
  calloutBatchAssociations$
} from './callouts.http.schema';
import { parseWithDecoratedError } from '../../../v4/entities/common/parseWithDecoratedError';
import { convertCoreFieldFiltersToHttp } from 'v5/core/core-fields/core-fields-filters.http-converters';
import { mergeWorkspaceScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeWorkspaceScopingIntoHttpFilters';
import { asGlobalCalloutsQuery } from './callouts.api';

// Map frontend sort fields to backend field names for callouts
const mapCalloutSortFieldToApi = (field: string): string => {
  const fieldMap: Record<string, string> = {
    title: 'title',
    slug: 'slug',
    createdAt: 'created_at',
    deadline: 'deadline'
  };
  return fieldMap[field] ?? 'created_at';
};

// Convert sorter to API format
const convertCalloutSorterToApi = (sorter: {
  field: string;
  order: 'asc' | 'desc';
}) => ({
  field: mapCalloutSortFieldToApi(sorter.field),
  direction: sorter.order.toUpperCase() as 'ASC' | 'DESC'
});

// Convert callout filters to API format
const convertCalloutFiltersToApi = buildFilterConverter({
  published: filter => (filter ? convertBooleanFilterToApi(filter) : null),
  slug: filter => (filter ? convertStringFilterToApi(filter) : null)
});

export function getCalloutsListRequest(
  query: CalloutsListQuery
): Record<string, unknown> {
  const scopedQuery: CalloutsQuery =
    'workspaceId' in query ? query : asGlobalCalloutsQuery(query);
  const filters = {
    // Convert callout filters to API format
    ...convertCoreFieldFiltersToHttp(scopedQuery.filters),
    ...convertCalloutFiltersToApi(scopedQuery.filters),
    ...mergeWorkspaceScopingIntoHttpFilters(scopedQuery)
  };

  return {
    archived: scopedQuery.filters?.archived?.value ? true : false,
    query: scopedQuery.search,
    order_by: scopedQuery.sort
      ? convertCalloutSorterToApi(scopedQuery.sort)
      : null,
    p: scopedQuery.page.page + 1, // API uses 1-based pagination
    count: scopedQuery.page.pageSize,
    filters
  };
}

export function createCalloutsApi(client: ApiClient): CalloutsApi {
  return {
    async getList(query: CalloutsListQuery, signal?: AbortSignal) {
      const request = getCalloutsListRequest(query);

      const response = await client.post(`/search/callouts`, {
        data: request,
        token: true,
        signal
      });

      // Validate response with schema
      const responseSchema = z.object({
        results: z.array(callout$),
        paging: z.object({
          total: z.number(),
          page: z.number(),
          count_per_page: z.number()
        })
      });

      const parsed = parseWithDecoratedError(responseSchema, response);

      return {
        items: parsed.results.map(calloutFromHttp),
        count: parsed.paging.total
      };
    },
    async getItem(id: Callout['id'], signal?: AbortSignal) {
      const response = await client.get(`/callouts/${id}`, {
        token: true,
        signal
      });

      const parsed = parseWithDecoratedError(callout$, response);
      return calloutFromHttp(parsed);
    },
    async getAssociations(id, signal) {
      const response = await client.get(`/callouts/${id}/associations`, {
        token: true,
        signal
      });

      // Validate response with schema
      const parsed = parseWithDecoratedError(calloutAssociations$, response);

      // The parsed response already includes the id from the schema
      return parsed;
    },
    async getBatchAssociations(ids: Callout['id'][], signal?: AbortSignal) {
      if (ids.length === 0) {
        return [];
      }

      // Sort and deduplicate IDs for canonical cache keys
      const uniqueIds = [...new Set(ids)].sort((a, b) => a - b);
      const idsString = uniqueIds.join(',');

      const response = await client.get(`/batch/callouts/associations`, {
        token: true,
        signal,
        params: {
          ids: idsString,
          expand: ['applicants'],
          excludeMainModel: true
        }
      });

      // Validate response with schema - now includes IDs
      const parsed = parseWithDecoratedError(
        calloutBatchAssociations$, // Array of associations with IDs
        response
      );

      // Response already includes IDs, just return as-is
      return parsed;
    },
    async getItemPublic(
      id: Callout['id'],
      query?: object,
      signal?: AbortSignal
    ) {
      const response = await client.get(`/public/callouts/${id}`, {
        signal,
        params: query
      });

      const responseSchema = z.object({
        public_settings: z.any(),
        callout: callout$
      });

      const parsed = parseWithDecoratedError(responseSchema, response);

      return {
        public_settings: parsed.public_settings,
        callout: calloutFromHttp(parsed.callout)
      };
    },
    async transferCalloutsToWorkspace(
      request: TransferCalloutsToWorkspaceRequest,
      signal?: AbortSignal
    ) {
      await client.post('/batch/callouts/transfer', {
        data: request,
        token: true,
        signal
      });
    }
  };
}
